import p5 from "p5";
import { rothko_palettes as palette_impt } from "./rothko_palettes.js";
import { quake_metadata } from "./quake_metadata.js";
// K
import {
  PHI,
  i_PHI, // I
  TWO_PI,
  frand_rng,
  arr_pick,
  fib_num, // R
  render_dims,
  fx_shuffle_arr,
  calculateAspectRatioFit,
  SQR_2,
  map_shift,
} from "./util.js";
// H
import {
  radial_out_mode,
  button_mode,
  ll_cross_mode,
  morse_code_mode,
  pieish_chart_mode,
} from "./render_modes.js";

import { project_description_for_console } from "./project_description_output.js";

const author = "sspboyd";
const sketch_name = "Boom_fx";

///////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////

let myp5 = new p5((p55) => {
  console.clear();

  let screen_pg;
  let c;
  const quake_md = arr_pick(quake_metadata);

  let q_data_geojson;

  // for vars that impact all iterations
  // S
  const config = {
    show_palette: 1,
    show_bbox: 0,
    show_timeline: 0,
    // blend: p55.HARD_LIGHT, // scene?
  };

  const render_modes = [];
  render_modes.push(radial_out_mode);
  render_modes.push(button_mode);
  render_modes.push(ll_cross_mode);
  render_modes.push(morse_code_mode);
  render_modes.push(pieish_chart_mode);

  let quakes = []; // an array of quake objects

  const set_scene = function () {
    // possible centre locations (from -1 to 1)
    const map_centre_locs = [-i_PHI, -1 * (1 - i_PHI), 1 - i_PHI, i_PHI]; // options for the lat/long offsets
    // set centre location of map
    const map_centre_norms = {
      x: p55.map(arr_pick(map_centre_locs), -1, 1, 0, 1),
      y: p55.map(arr_pick(map_centre_locs), -1, 1, 0, 1),
    };

    const map_centre_offset_norms = {
      x: map_centre_norms.x - 0.5,
      y: map_centre_norms.y - 0.5,
    };

    // Colour palette
    const palette_md = arr_pick(palette_impt); // _md means metadata
    const pal_idx = palette_impt.findIndex((p) => p.title === palette_md.title);
    const palette_length = palette_impt[pal_idx].palette.length;
    const palette = palette_md.palette;
    const palette_title = palette_md.title;

    // Time of day variables
    // This produces 6 somewhat evenly spaced out values
    // (1 - i_PHI) * i_PHI, ==> 0.382 * 0.618 ==> 0.2361
    const dur_exp_fact = fib_num(
      0,
      (1 - i_PHI) * i_PHI,
      Math.floor(frand_rng(6))
    );
    const scene_dur_mins = Math.floor(
      Math.pow(1 - i_PHI, dur_exp_fact) * (1 * 24 * 60)
    );
    const scene_start_mins = Math.floor(
      frand_rng(0, 1 * 24 * 60 - scene_dur_mins)
    ); // in minutes

    const date = new Date(quake_md.date);
    const date_desc = quake_md.desc;
    const dt_dur_mins = scene_dur_mins;
    const dt_start_mins = scene_start_mins;
    const dt_end_mins = scene_start_mins + scene_dur_mins;

    const aspect = {};
    aspect.name = "Square";
    aspect.layout = "square";
    aspect.ratio = { w: 1, h: 1 };

    const aspect_ratio_fact = frand_rng(1);
    if (aspect_ratio_fact > 1 - i_PHI) {
      aspect.name = "Landscape";
      aspect.layout = "land";
      aspect.ratio.w = SQR_2;
      aspect.ratio.h = 1;
    } else if (aspect_ratio_fact < Math.pow(i_PHI, 3)) {
      aspect.name = "Portrait";
      aspect.layout = "port";
      aspect.ratio.w = 1;
      aspect.ratio.h = SQR_2;
    }
    aspect.dims = render_dims[aspect.layout];

    return {
      aspect,
      date,
      date_desc,
      dt_dur_mins,
      dt_start_mins,
      dt_end_mins,
      map_centre_norms,
      map_centre_offset_norms,
      palette,
      palette_length,
      pal_idx,
      palette_title,
    };
  };
  const scene = set_scene(); // this will hold info that is specific to each iteration. eg clr palettes, date, duration...

  window.$fxhashFeatures = {
    "On this date": scene.date_desc,
    "Aspect Ratio": scene.aspect.name,
    "Colour Palette": scene.palette_title,
    "Concurrent minutes sampled during this day": scene.dt_dur_mins,
  };

  p55.preload = () => {
    q_data_geojson = p55.loadJSON(quake_md.filename);
  };

  p55.setup = () => {
    p55.pixelDensity(1);

    // Create quake objs with data from geojson feed.
    let curr_quakes = parse_q_data(q_data_geojson);
    quakes = init_quakes(curr_quakes);
    scene.num_quakes = quakes.length;
    scene.centre_loc = { x: frand_rng(-180, 180), y: frand_rng(-90, 90) }; // random

    let screen_dims = calculateAspectRatioFit(
      scene.aspect.ratio.w,
      scene.aspect.ratio.h,
      window.innerWidth,
      window.innerHeight
    );

    screen_dims.x = 2 * Math.round(screen_dims.x / 2);
    screen_dims.y = 2 * Math.round(screen_dims.y / 2);

    render_dims.screen = screen_dims;
    scene.aspect.dims.screen = screen_dims;

    c = p55.createCanvas(
      scene.aspect.dims.screen.x,
      scene.aspect.dims.screen.y
    );

    screen_pg = p55.createGraphics(
      scene.aspect.dims.screen.x,
      scene.aspect.dims.screen.y
    );
    // screen_pg.blendMode(config.blend);
    screen_pg.strokeCap(p55.PROJECT);

    console.time("Time for One Render");
    p55.image(render(scene.aspect.dims.screen, screen_pg), 0, 0);
    console.timeEnd("Time for One Render");
    p55.noLoop();
    console.log(`Scene values:`);
    console.log({ scene });

    fxpreview();
    project_description_for_console();
    // export to file
    // export_img(p55, scene.aspect.dims.insta);
  };

  p55.draw = () => {
    // render to web browser canvas
    // p55.image(render(scene.aspect.dims.screen, screen_pg), 0, 0);
  };

  ///////////////////////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////////////

  const render = function (curr_dims, pg) {
    pg.clear();
    pg.background(scene.palette[scene.palette.length - 1]);
    if (config.show_palette) {
      const max_dim = Math.max(curr_dims.x, curr_dims.y);
      const swatch = {
        w: max_dim,
        h: max_dim,
      };
      const old_centre = map_shift(scene.map_centre_offset_norms, {
        x: 0.5,
        y: 0.5,
        z: 0,
      });
      pg.noStroke();
      pg.fill(scene.palette[0]);
      // SW quadrant
      pg.rect(
        old_centre.x * curr_dims.x,
        old_centre.y * curr_dims.y,
        -swatch.w,
        swatch.h
      );
      pg.fill(scene.palette[1]);
      // SE quadrant
      pg.rect(
        old_centre.x * curr_dims.x,
        old_centre.y * curr_dims.y,
        swatch.w,
        swatch.h
      );
    }

    // Render quakes
    for (let i = 0; i < quakes.length; i++) {
      const q = quakes[i];
      q.update(curr_dims);
      q.render(q, curr_dims, pg);
    }

    // Debug options here:
    if (config.show_bbox) {
      const cbx = get_bbox(quakes); // current bbox
      render_bbox(cbx, pg);
    }

    if (config.show_timeline) {
      pg.stroke(255, 123);
      pg.strokeWeight(2);
      pg.line(11, curr_dims.y - 11, curr_dims.x - 11, curr_dims.y - 11);
      let t_box = {
        x1: p55.map(scene.dt_start_mins, 0, 1440, 11, curr_dims.y - 11),
        x2: p55.map(
          scene.dt_start_mins + scene.dt_dur_mins,
          0,
          1440,
          11,
          curr_dims.y - 11
        ),
        y: curr_dims.y - 11,
        h: 11,
      };
      pg.fill(0);
      pg.noStroke();
      pg.rect(t_box.x1, t_box.y - 4, t_box.x2 - t_box.x1, 7);
    }
    return pg;
  };

  // cbx = current bbox object
  const render_bbox = function (cbx, pg) {
    pg.stroke(255);
    pg.strokeWeight(1);
    pg.noFill();
    pg.rect(
      cbx.x.min * pg.width,
      cbx.y.min * pg.height,
      cbx.w * pg.width,
      cbx.h * pg.height
    );
    pg.fill(255);
    pg.ellipse(cbx.mid.x * pg.width, cbx.mid.y * pg.height, 10, 10);
  };

  // returns an array of objs filtered to only include events within the duration window
  const parse_q_data = function (data_src) {
    let curr_quakes = [];

    for (let i = 0; i < data_src.features.length; i++) {
      let curr_q = data_src.features[i];
      let lld_loc = p55.createVector(
        curr_q.geometry.coordinates[0],
        curr_q.geometry.coordinates[1],
        curr_q.geometry.coordinates[2]
      );
      let mag = curr_q.properties.mag;
      let dt = new Date(curr_q.properties.time);
      let dt_ms = curr_q.properties.time;

      // normalized location values
      let norm_lat = p55.map(lld_loc.x, -180, 180, 0, 1);
      let norm_lon = p55.map(lld_loc.y, -90, 90, 0, 1);
      let norm_depth = p55.map(lld_loc.z, 0, 1000, 0, 1);
      let norm_loc = p55.createVector(norm_lat, norm_lon, norm_depth);

      let render_loc = p55.createVector();
      let q_name = curr_q.properties.title;
      let q_id = curr_q.id;
      let loc = { lld: lld_loc, norm: norm_loc, render: render_loc };

      let q = { dt, dt_ms, loc, mag, q_name, q_id };
      curr_quakes.push(q);
    }

    // filter by date
    const date_filtered_quakes = curr_quakes.filter(function (cq) {
      let in_range = true;
      const start_time_ms =
        scene.date.getTime() + scene.dt_start_mins * 60 * 1000;
      const end_time_ms = start_time_ms + scene.dt_dur_mins * 60 * 1000;
      if (cq.dt_ms < start_time_ms) in_range = false; // quake before start time??
      if (cq.dt_ms > end_time_ms) in_range = false; // quake after end time??
      return in_range;
    });

    console.group("Quake Metadata:");
    console.log(quake_md.desc);
    console.log(
      `Estimated number of quakes expected during this amount of time: ${Math.round(
        (curr_quakes.length / 1440) * scene.dt_dur_mins
      )}`
    );
    console.log(
      `Actual number of quakes in this time: ${date_filtered_quakes.length}`
    );

    // find max mag in the filtered set
    scene.max_mag = Math.max(...date_filtered_quakes.map((q) => q.mag));
    console.log(`Maximum magnitude represented: ${scene.max_mag}M`);
    // find max depth in the filtered set
    scene.max_depth = Math.max(...date_filtered_quakes.map((q) => q.loc.lld.z));
    console.log(
      `Maximum depth represented: ${Math.round(scene.max_depth)} kms`
    );
    // find min depth in the filtered set
    scene.min_depth = Math.min(...date_filtered_quakes.map((q) => q.loc.lld.z));
    console.groupEnd();

    // sort the json array by mag and then date
    const mag_date_quakes = date_filtered_quakes.sort(
      (q_a, q_b) => q_b.mag - q_a.mag || q_a.dt_ms - q_b.dt_ms
    );

    mag_date_quakes.forEach((q) => {
      q.render = gen_rdr_mode(render_modes, q); // assigns a render mode obj to the quake
    });
    return mag_date_quakes;
  };

  const gen_rdr_mode = function (r_modes, q) {
    const curr_rdr_m = arr_pick(r_modes);
    const rdr_m = curr_rdr_m.init_render(p55, scene, q);
    return rdr_m;
  };

  // adds update method
  const init_quakes = function (curr_quakes) {
    // add update method
    // this made more sense when the whole thing would be animating
    curr_quakes.forEach((q) => {
      q.update = function (curr_dims) {
        // if scene area has changed then update x/y locations
        const map_shifted_loc = map_shift(
          scene.map_centre_offset_norms,
          q.loc.norm
        );
        q.loc.render.x = map_shifted_loc.x * curr_dims.x;
        q.loc.render.y = map_shifted_loc.y * curr_dims.y;
      };
    });
    return curr_quakes;
  };

  const get_bbox = function (q_arr) {
    const bbox = {};
    bbox.x = {
      min: Math.min(...q_arr.map((q) => q.loc.norm.x)),
      max: Math.max(...q_arr.map((q) => q.loc.norm.x)),
    };
    bbox.y = {
      min: Math.min(...q_arr.map((q) => q.loc.norm.y)),
      max: Math.max(...q_arr.map((q) => q.loc.norm.y)),
    };
    bbox.hbd2m =
      "🎂🎉🎂🎉🎂🎉🎂🎉🎂🎉🎂🎉🎂🎉🎂🎉🎂🎉🎂🎉🎂🎉🎂🎉🎂🎉🎂🎉🎂🎉🎂🎉🎂🎉🎂🎉🎂🎉🎂🎉🎂🎉🎂🎉🎂🎉🎂🎉🎂🎉🎂🎉🎂🎉🎂🎉🎂🎉🎂🎉🎂🎉🎂🎉🎂🎉🎂🎉🎂🎉🎂🎉🎂🎉🎂🎉🎂🎉🎂🎉🎂🎉🎂🎉🎂🎉🎂🎉🎂🎉🎂🎉🎂🎉🎂🎉🎂🎉";
    bbox.w = bbox.x.max - bbox.x.min;
    bbox.h = bbox.y.max - bbox.y.min;
    bbox.mid = {
      x: bbox.w / 2 + bbox.x.min,
      y: bbox.h / 2 + bbox.y.min,
    };
    bbox.ratio = bbox.w / bbox.h;
    return bbox;
  };

  ///////////////////////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////////////

  p55.keyTyped = () => {
    keyboard_input(p55, p55.key, render_dims);
  };

  /**
   * This function handles keyboard input
   * @param {*} p55
   * @param {*} key
   * @param {*} render_dims
   */
  const keyboard_input = function (p55, key, render_dims) {
    const ca_dims = scene.aspect.dims; // ca_dims = current aspect dimensions
    console.log(ca_dims.insta.x);
    switch (key) {
      case "T":
      case "t":
        console.group(
          `Saving thumbnail at ${ca_dims.thumb.x} x ${ca_dims.thumb.y}`
        );
        export_img(p55, ca_dims.thumb);
        console.groupEnd();
        break;
      case "I":
      case "i":
        console.group(
          `Saving thumbnail at ${ca_dims.insta.x} x ${ca_dims.insta.y} for Instagram (1:1).`
        );
        export_img(p55, ca_dims.insta);
        console.groupEnd();
        break;
      case "S":
      case "s":
        console.group(
          `Saving screen size at ${ca_dims.screen.x} x ${ca_dims.screen.y}`
        );
        export_img(p55, ca_dims.screen);
        console.groupEnd();
        break;
      case "P":
      case "p":
        console.group(
          `Saving print (16" @ 300dpi) at ${ca_dims.print.x} x ${ca_dims.print.y}`
        );
        export_img(p55, ca_dims.print);
        console.groupEnd();
        break;
      case "M":
      case "m":
        console.group(
          `Saving at ${ca_dims.max.x} x ${ca_dims.max.y}. Really?? This is ridiculously large. What are you thinking??!?!?`
        );
        export_img(p55, ca_dims.max);
        console.groupEnd();
        break;
      case "H":
      case "h":
        console.group(
          `Saving at 4K on the vertial at ${ca_dims.hd4k.x} x ${ca_dims.hd4k.y}`
        );
        export_img(p55, ca_dims.hd4k);
        console.groupEnd();
        break;
      default:
        console.log(
          "Hmm. Your keyboard input is not recognized. Try using S, P, M or H to export to PNG. \nOr A to toggle animation."
        );
    }
  };

  /**
   * This function renders and image and saves it out
   * @param {*} p55
   * @param {*} dims
   */
  const export_img = function (p55, dims) {
    const export_pg = p55.createGraphics(dims.x, dims.y);
    // export_pg.blendMode(config.blend);
    export_pg.strokeCap(p55.PROJECT);

    const sk_name = sketch_name;
    const res = `${dims.x}x${dims.y}`;
    // generate date string: YYYY-MM-DD-HH-MM-SS
    const dt = new Date(); // convert this to UTC?
    const datetime = `${dt.getFullYear().toString()}${(dt.getMonth() + 1)
      .toString()
      .padStart(2, "0")}${dt.getDate().toString().padStart(2, "0")}${dt
      .getHours()
      .toString()
      .padStart(2, "0")}${dt.getMinutes().toString().padStart(2, "0")}${dt
      .getSeconds()
      .toString()
      .padStart(2, "0")}${dt.getMilliseconds().toString().padStart(3, "0")}`;
    const filename = `${sk_name}-by_${author}-${datetime}-${res}-${fxhash}`;
    console.log(`saving image to: ${filename}`);
    const output_img = render(dims, export_pg);
    output_img.save(filename);
  };
});
